import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, map, Observable, of } from 'rxjs';

// Services
import { NetworkService } from '@modules/core/services/network.service';
import { StateService } from '@modules/settings/services/state.service';
import { RecordsService } from '@modules/records/services/records.service';

// Types
import { Clip } from '../types/clip';
import { ClipPoints } from '../types/clip-points';
import { Record } from '@modules/records/types/record';

// Decorators
import { warmUpObservable } from '@decorators';
import { StreamSettings } from '../types/stream-settings';
import { SrtStats } from '../types/srt-stats';


export type StreamStatusPlaybackLoop = 'off' | 'in_out' | 'continuous';

@Injectable({
  providedIn: 'root'
})
export class PreviewService {

  /**
   * Constructor
   */

  constructor(
    private networkService: NetworkService,
  ) {
  }

  /**
   * Playback
   */

  @warmUpObservable
  play(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'play', { serverIds });
  }

  @warmUpObservable
  reverse(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'reverse', { serverIds });
  }

  @warmUpObservable
  pause(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'pause', { serverIds });
  }

  @warmUpObservable
  fastForward(recordId: string, serverIds: string[], speed: number = 0.5): Observable<void> {
    return this.networkService.playback(recordId, 'fast_forward', { speed, serverIds });
  }

  @warmUpObservable
  rewind(recordId: string, serverIds: string[], speed: number = 0.5): Observable<void> {
    return this.networkService.playback(recordId, 'rewind', { speed, serverIds });
  }

  @warmUpObservable
  goToStart(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_start', { serverIds });
  }

  @warmUpObservable
  goToEnd(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_end', { serverIds });
  }

  @warmUpObservable
  setInPoint(recordId: string, serverIds: string[], value?: {tc?: string, frame?: number}): Observable<void> {
    // Without param – set current timecode
    // tc - string timecode "00:00:00:00"
    // or
    // frame - number or frame
    const params: any = { serverIds };
    if (value?.tc) {
      params['tc'] = value.tc;
    } else if (value && 'frame' in value) {
      params['frame'] = value.frame;
    }
    return this.networkService.playback(recordId, 'set_in_point', params);
  }

  @warmUpObservable
  setOutPoint(recordId: string, serverIds: string[], value?: {tc?: string, frame?: number}): Observable<void> {
    // Without param – set current timecode
    // tc - string timecode "00:00:00:00"
    // or
    // frame - number or frame
    const params: any = { serverIds };
    if (value?.tc) {
      params['tc'] = value.tc;
    } else if (value && 'frame' in value) {
      params['frame'] = value.frame;
    }
    return this.networkService.playback(recordId, 'set_out_point', params);
  }

  @warmUpObservable
  jump(recordId: string, serverIds: string[], value: {tc?: string, frame?: number, offset?: number}): Observable<void> {
    // tc - string timecode "00:00:00:00"
    // or
    // frame - number (jump to frame)
    // or
    // offset - number (like 10 or -10)
    const params: any = { serverIds };
    if (value.tc) {
      params['tc'] = value.tc;
    } else if ('frame' in value) {
      params['frame'] = value.frame;
    } else if ('offset' in value) {
      params['offset'] = value.offset;
    }
    return this.networkService.playback(recordId, 'jump', params);
  }

  @warmUpObservable
  openFile(recordId: string, serverIds: string[], clipIds?: number[]): Observable<void> {
    const params = (clipIds?.length || 0) > 0
      ? (clipIds?.length === 1 ? {clipId: clipIds[0]} : {clips: clipIds, serverIds})
      : { serverIds };
    return this.networkService.playback(recordId, 'open_file', params);
  }

  @warmUpObservable
  eject(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'eject', { serverIds });
  }

  @warmUpObservable
  goToNextClip(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_next_clip', { serverIds });
  }

  @warmUpObservable
  goToPrevClip(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_prev_clip', { serverIds });
  }

  @warmUpObservable
  hideStopOverlay(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'hide_stop_overlay', { serverIds });
  }

  @warmUpObservable
  loopPlayback(recordId: string, serverIds: string[], mode: StreamStatusPlaybackLoop): Observable<void> {
    return this.networkService.playback(recordId, 'loop_playback', { mode, serverIds });
  }

  @warmUpObservable
  goToNextAndPause(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_next_and_pause', { serverIds });
  }

  @warmUpObservable
  goToNextAndPlay(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_next_and_play', { serverIds });
  }

  @warmUpObservable
  goToPrevAndPause(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_prev_and_pause', { serverIds });
  }

  @warmUpObservable
  goToPrevAndPlay(recordId: string, serverIds: string[]): Observable<void> {
    return this.networkService.playback(recordId, 'go_to_prev_and_play', { serverIds });
  }

  @warmUpObservable
  goToClipByIndexAndPause(recordId: string, serverIds: string[], index: any): Observable<void> {
    return this.networkService.playback(recordId, 'go_clip_by_index_and_pause', { serverIds });
  }

  /**
   * Clips
   */

  getIndexedFiles(recordId: string, serverIds: string[], options?: {path?: string[], ids?: number[]}): Observable<Clip[]> {
    let params: any = { serverIds };
    if (options?.path && options?.path?.length) {
      params['path'] = options.path;
    }
    if (options?.ids && options?.ids?.length) {
      params['id'] = options?.ids;
    }

    return this.networkService.getIndexedFiles(recordId, params);
  }

  getClip(recordId: string, serverIds: string[], path: string): Observable<Clip | undefined> {
    if (!path || !path.length) {
      return of();
    }
    return this.networkService.getClip(recordId, serverIds, path);
  }

  getSettings(recordId: string, serverIds: string[], channel: number): Observable<StreamSettings | undefined> {
    return this.networkService.getSettings(recordId, serverIds, channel);
  }

  getSrtStats(recordId: string, serverId: string): Observable<SrtStats> {
    return this.networkService.getSrtStats(recordId, serverId);
  }

}
